import { Vector3 } from 'three';

import { Bone, Skeleton } from '../skeleton/core';
import {
  EXPECTED_CHILD_COUNTS,
  EXPECTED_DEPTHS,
  ROLE_TERMS,
  isAccessoryBranchLikeName,
  isArmLikeName,
  isDeformationHelperLikeName,
  isFingerHelperLikeName,
  isGenericNumberedPlaceholderJointName,
  isHandLikeName,
  isHeadLikeName,
  isLegLikeName,
  isNeckLikeName,
  isPelvisLikeName,
  isSpineLikeName,
  type SideConvention,
} from '../schema/mapping';
import {
  type ScoreBreakdown,
  type ScoringProfile,
  topologyScore,
  namingScore,
  depthScore,
  directionScore,
  totalScore,
} from './scoring';
import type { SkeletonSpatialData } from './spatial';
import {
  type CheckContext,
  evaluateRole,
  EvaluationResult,
} from './checks';
import { CandidateInfo, AUX_PATTERNS, EXCLUDE_PATTERNS } from './config';

// ─── Position helpers ────────────────────────────────────────────────────

function boneWorldPos(bone: Bone): Vector3 {
  const e = bone.restWorld.elements;
  return new Vector3(e[12], e[13], e[14]);
}

function boneWorldY(bone: Bone): number {
  return bone.restWorld.elements[13];
}

function boneWorldX(bone: Bone): number {
  return bone.restWorld.elements[12];
}

// ─── Chain direction (inlined from chains module) ────────────────────────

function chainDirection(skel: Skeleton, chain: number[]): Vector3 {
  if (chain.length === 0) return new Vector3();
  const sum = new Vector3();
  let count = 0;
  for (const idx of chain) {
    const b = skel.bone(idx);
    if (b.length > 0) {
      sum.add(b.direction);
      count++;
    }
  }
  if (count === 0) return new Vector3();
  sum.divideScalar(count);
  const norm = sum.length();
  if (norm < 1e-8) return new Vector3();
  return sum.divideScalar(norm);
}

// ─── Constants ───────────────────────────────────────────────────────────

const PERFECT_INLINE_ANGLE = 1.0;

const CRITICAL_JOINTS = new Set([
  'shoulder', 'elbow', 'wrist', 'hand', 'forearm', 'arm',
  'knee', 'ankle', 'foot', 'thigh', 'shin', 'calf', 'leg', 'femur', 'tibia',
  'spine', 'chest', 'neck', 'head',
  'thumb', 'index', 'middle', 'mid', 'ring', 'pinky', 'little', 'finger', 'fin',
  'hombro', 'brazo', 'antebrazo',
  'rodilla', 'pie', 'pierna', 'cadera',
  'espina', 'cuello', 'cabeza', 'cola',
  'épaule', 'epaule', 'bras', 'avant-bras', 'avantbras', 'avant_bras',
  'genou', 'pied', 'jambe', 'hanche', 'bassin',
  'colonne', 'vertébrale', 'vertebrale', 'cou', 'tête', 'tete',
  'queue', 'doigt', 'orteil',
  'ひじ', 'ひざ', '手首', '足首', '親指', '人指', '中指', '薬指', '小指',
  '上半身', '下半身', '首', '頭', '肩',
]);

const JOINT_KEYWORDS = new Set([
  'elbow', 'forearm', 'lowerarm', 'lower_arm', 'wrist', 'hand', 'arm',
  'knee', 'calf', 'shin', 'lowerleg', 'lower_leg', 'ankle', 'foot', 'toe', 'toes', 'ball', 'leg',
  'shoulder', 'hip', 'thigh', 'upper', 'head', 'neck', 'spine',
  'hombro', 'brazo', 'antebrazo',
  'rodilla', 'pie', 'pierna', 'cadera',
  'espina', 'cuello', 'cabeza', 'cola',
  'épaule', 'epaule', 'bras', 'avant-bras', 'avantbras', 'avant_bras',
  'genou', 'pied', 'jambe', 'hanche', 'bassin',
  'colonne', 'vertébrale', 'vertebrale', 'cou', 'tête', 'tete',
  'queue', 'doigt', 'orteil',
  'ひじ', 'ひざ', '手首', '足首',
]);

const HELPER_BONE_PATTERNS = new Set([
  'part', 'twist', 'roll', 'aux', 'helper', 'sub', 'segment', 'split',
]);

const STRUCTURAL_PARENT_PATTERNS = [
  'clavicle', 'clavicula', 'clavical', 'collar', 'hombro',
  'épaule', 'epaule',
  'upperarm', 'upper_arm', 'uparm', 'brazo',
  'bras',
  'pelvis', 'hip', 'cadera', 'hanche', 'bassin',
  'thigh', 'upperleg', 'upper_leg', 'upleg', 'pierna', 'jambe',
];

const _SUBDIVISION_PATTERN = /_\d{2,3}_\d/;

// ─── Name-checking utilities ─────────────────────────────────────────────

export function _isSubdivisionBone(name: string): boolean {
  return _SUBDIVISION_PATTERN.test(name);
}

function _isHelperBoneName(name: string): boolean {
  if (isFingerHelperLikeName(name)) return true;
  const lower = name.toLowerCase();
  for (const pat of HELPER_BONE_PATTERNS) {
    if (lower.includes(pat)) return true;
  }
  return false;
}

function _isProtectedLimbBone(skel: Skeleton, bone: Bone): boolean {
  if (bone.parent === null) return false;
  const parent = skel.bone(bone.parent);
  if (parent.auxiliary) return false;

  if (/_\d{2,}_/.test(bone.name)) return false;

  const parentLower = parent.name.toLowerCase();
  const isStructuralParent = STRUCTURAL_PARENT_PATTERNS.some((kw) =>
    parentLower.includes(kw),
  );
  if (!isStructuralParent) return false;

  const nonAuxChildren = parent.children.filter(
    (c) => !skel.bone(c).auxiliary,
  );
  return nonAuxChildren.length === 1 && nonAuxChildren[0] === bone.index;
}

function _isInFingerChain(
  skel: Skeleton,
  bone: Bone,
  maxChainDepth = 4,
): boolean {
  if (bone.length > 15.0) return false;

  let current: Bone = bone;
  let depth = 0;

  while (depth < maxChainDepth && current.parent !== null) {
    const parent = skel.bone(current.parent);
    if (parent.auxiliary) {
      current = parent;
      depth++;
      continue;
    }

    const nonAuxChildren = parent.children.filter(
      (c) => !skel.bone(c).auxiliary,
    );
    if (nonAuxChildren.length >= 4) {
      if (parent.subtreeSize > 30) {
        current = parent;
        depth++;
        continue;
      }
      if (parent.depth < 7) {
        current = parent;
        depth++;
        continue;
      }

      let fingerChainCount = 0;
      let maxChildSubtree = 0;
      for (const childIdx of nonAuxChildren) {
        const child = skel.bone(childIdx);
        if (child.length >= 20.0) continue;
        if (child.children.length === 0) continue;
        maxChildSubtree = Math.max(maxChildSubtree, child.subtreeSize);
        if (child.subtreeSize <= 6) fingerChainCount++;
      }

      if (maxChildSubtree > 6) {
        current = parent;
        depth++;
        continue;
      }
      if (fingerChainCount >= 4) return true;
    }

    current = parent;
    depth++;
  }
  return false;
}

function _isInVerticalChain(
  skel: Skeleton,
  bone: Bone,
  minVerticalExtent = 100.0,
  skeletonHeight: number | null = null,
): boolean {
  let effectiveMinExtent: number;
  let downwardThreshold: number;

  if (skeletonHeight !== null && skeletonHeight > 1e-6) {
    effectiveMinExtent = 0.20 * skeletonHeight;
    downwardThreshold = 0.05 * skeletonHeight;
  } else {
    effectiveMinExtent = minVerticalExtent;
    downwardThreshold = 10.0;
  }

  const boneY = boneWorldY(bone);
  let highestY = boneY;
  let currentIdx: number | null = bone.parent;
  let steps = 0;
  const maxUpwardSteps = 5;

  while (currentIdx !== null && currentIdx >= 0 && steps < maxUpwardSteps) {
    const ancestor = skel.bone(currentIdx);
    if (ancestor.auxiliary) break;
    const ay = boneWorldY(ancestor);
    if (ay > highestY) highestY = ay;
    currentIdx = ancestor.parent;
    steps++;
  }

  const findLowestY = (startIdx: number, depth = 0): number => {
    if (depth > 5) return boneWorldY(skel.bone(startIdx));
    const b = skel.bone(startIdx);
    let minY = boneWorldY(b);
    for (const childIdx of b.children) {
      const child = skel.bone(childIdx);
      if (child.auxiliary) continue;
      const childLow = findLowestY(childIdx, depth + 1);
      if (childLow < minY) minY = childLow;
    }
    return minY;
  };

  const lowestY = findLowestY(bone.index);
  const verticalExtent = highestY - lowestY;

  let childGoesDown = false;
  for (const childIdx of bone.children) {
    const child = skel.bone(childIdx);
    if (!child.auxiliary) {
      if (boneWorldY(child) < boneY - downwardThreshold) {
        childGoesDown = true;
        break;
      }
    }
  }

  return verticalExtent > effectiveMinExtent && childGoesDown;
}

function _isInUpwardCenteredChain(
  skel: Skeleton,
  bone: Bone,
  minVerticalExtent = 50.0,
  centerToleranceRatio = 0.05,
  skeletonHeight: number | null = null,
): boolean {
  const effectiveMinExtent =
    skeletonHeight !== null && skeletonHeight > 1e-6
      ? 0.15 * skeletonHeight
      : minVerticalExtent;

  const boneY = boneWorldY(bone);
  const boneX = boneWorldX(bone);

  if (bone.parent === null || bone.parent < 0) return false;
  const parent = skel.bone(bone.parent);
  const parentY = boneWorldY(parent);
  if (boneY <= parentY) return false;

  const allX: number[] = [];
  for (const b of skel.bones) {
    if (!b.auxiliary) allX.push(boneWorldX(b));
  }
  if (allX.length === 0) return false;

  const xMin = Math.min(...allX);
  const xMax = Math.max(...allX);
  const skeletonWidth = xMax - xMin;
  const skeletonCenterX = (xMax + xMin) / 2;
  const centerTolerance = skeletonWidth * centerToleranceRatio;

  if (Math.abs(boneX - skeletonCenterX) > centerTolerance) return false;

  let highestY = boneY;
  let currentIdx: number | null = bone.index;
  let steps = 0;
  const maxSteps = 10;

  while (steps < maxSteps) {
    const current = skel.bone(currentIdx!);
    let upwardChild: number | null = null;
    for (const childIdx of current.children) {
      const child = skel.bone(childIdx);
      if (child.auxiliary) continue;
      const childY = boneWorldY(child);
      const childX = boneWorldX(child);
      if (childY > boneWorldY(current)) {
        if (Math.abs(childX - skeletonCenterX) <= centerTolerance) {
          upwardChild = childIdx;
          if (childY > highestY) highestY = childY;
          break;
        }
      }
    }
    if (upwardChild === null) break;
    currentIdx = upwardChild;
    steps++;
  }

  let lowestY = parentY;
  currentIdx = bone.parent;
  steps = 0;

  while (currentIdx !== null && currentIdx >= 0 && steps < maxSteps) {
    const current = skel.bone(currentIdx);
    const currentY = boneWorldY(current);
    const currentX = boneWorldX(current);
    if (Math.abs(currentX - skeletonCenterX) > centerTolerance) break;
    if (currentY < lowestY) lowestY = currentY;
    currentIdx = current.parent;
    steps++;
  }

  return highestY - lowestY > effectiveMinExtent;
}

function _isInSignificantChain(
  skel: Skeleton,
  bone: Bone,
  skeletonHeight: number | null = null,
): boolean {
  let skelDiagonal: number;

  if (skeletonHeight !== null && skeletonHeight > 1e-6) {
    skelDiagonal = skeletonHeight * 1.2;
  } else {
    const mins = new Vector3(Infinity, Infinity, Infinity);
    const maxs = new Vector3(-Infinity, -Infinity, -Infinity);
    for (const b of skel.bones) {
      const p = boneWorldPos(b);
      mins.min(p);
      maxs.max(p);
    }
    skelDiagonal = maxs.clone().sub(mins).length();
    if (skelDiagonal < 1e-6) return false;
  }

  const minExtent = 0.15 * skelDiagonal;

  const positions: Vector3[] = [boneWorldPos(bone)];

  let currentIdx: number | null = bone.parent;
  const maxSteps = 8;
  let steps = 0;
  while (currentIdx !== null && currentIdx >= 0 && steps < maxSteps) {
    const ancestor = skel.bone(currentIdx);
    if (ancestor.auxiliary) break;
    positions.push(boneWorldPos(ancestor));
    currentIdx = ancestor.parent;
    steps++;
  }

  let curIdx = bone.index;
  steps = 0;
  while (steps < maxSteps) {
    const cur = skel.bone(curIdx);
    const nonAuxKids = cur.children.filter((c) => !skel.bone(c).auxiliary);
    if (nonAuxKids.length !== 1) break;
    const child = skel.bone(nonAuxKids[0]);
    if (child.auxiliary) break;
    positions.push(boneWorldPos(child));
    curIdx = nonAuxKids[0];
    steps++;
  }

  if (positions.length < 3) return false;

  const pMin = new Vector3(Infinity, Infinity, Infinity);
  const pMax = new Vector3(-Infinity, -Infinity, -Infinity);
  for (const p of positions) {
    pMin.min(p);
    pMax.max(p);
  }
  const extent = pMax.clone().sub(pMin).length();
  return extent > minExtent;
}

// ─── Leaf / inline passthrough marking ───────────────────────────────────

function _markHelperLeafBones(skel: Skeleton): void {
  for (const bone of skel.bones) {
    if (bone.auxiliary) continue;
    if (!_isHelperBoneName(bone.name)) continue;
    const nonAux = bone.children.filter((c) => !skel.bone(c).auxiliary);
    if (nonAux.length === 0) bone.auxiliary = true;
  }
}

function _markInlinePassthroughBones(
  skel: Skeleton,
  maxInlineAngle = 15.0,
  helperMaxAngle = 45.0,
): void {
  const allY = skel.bones.filter((b) => !b.auxiliary).map((b) => boneWorldY(b));
  const skeletonHeight = allY.length > 0 ? Math.max(...allY) - Math.min(...allY) : 0;

  for (const bone of skel.bones) {
    if (bone.auxiliary) continue;
    if (bone.parent === null) continue;

    const parent = skel.bone(bone.parent);
    if (parent.auxiliary) continue;

    const nonAuxChildren = bone.children.filter(
      (c) => !skel.bone(c).auxiliary,
    );
    if (nonAuxChildren.length !== 1) continue;

    const boneDir = bone.direction;
    const parentDir = parent.direction;
    const normBone = boneDir.length();
    const normParent = parentDir.length();
    if (normBone < 1e-8 || normParent < 1e-8) continue;

    const boneDirNorm = boneDir.clone().divideScalar(normBone);
    const parentDirNorm = parentDir.clone().divideScalar(normParent);
    const dot = boneDirNorm.dot(parentDirNorm);
    const angle =
      Math.acos(Math.max(-1.0, Math.min(1.0, dot))) * (180.0 / Math.PI);

    const nameLower = bone.name.toLowerCase();
    const isHelper = _isHelperBoneName(bone.name);
    const isFingerHelper = isFingerHelperLikeName(nameLower);
    const hasJointKeyword = [...JOINT_KEYWORDS].some((kw) =>
      nameLower.includes(kw),
    );
    const isCriticalJoint = [...CRITICAL_JOINTS].some((kw) =>
      nameLower.includes(kw),
    );

    if (isCriticalJoint && !isHelper) continue;
    if (_isProtectedLimbBone(skel, bone)) continue;

    if (!isHelper) {
      if (_isInVerticalChain(skel, bone, undefined, skeletonHeight))
        continue;
      if (_isInUpwardCenteredChain(skel, bone, undefined, undefined, skeletonHeight))
        continue;
      if (_isInSignificantChain(skel, bone, skeletonHeight)) continue;
    }

    if (isFingerHelper) {
      bone.auxiliary = true;
      continue;
    }

    if (_isInFingerChain(skel, bone)) continue;

    if (isHelper) {
      if (angle < helperMaxAngle) bone.auxiliary = true;
    } else if (angle < PERFECT_INLINE_ANGLE) {
      bone.auxiliary = true;
    } else if (!hasJointKeyword && angle < maxInlineAngle) {
      bone.auxiliary = true;
    }
  }
}

function _isProtectedGenericLeafEndpoint(
  skel: Skeleton,
  bone: Bone,
  eps: number,
  requireDeforming = false,
): boolean {
  if (requireDeforming && !bone.deforming) return false;
  if (!isGenericNumberedPlaceholderJointName(bone.name)) return false;
  if (bone.parent === null || bone.parent < 0) return false;

  const nonAuxChildren = bone.children.filter((c) => !skel.bone(c).auxiliary);
  if (nonAuxChildren.length !== 0) return false;

  const parent = skel.bone(bone.parent);
  if (parent.auxiliary || !parent.deforming || parent.length < eps) return false;
  if (parent.parent === null || parent.parent < 0) return false;

  const parentNonAuxChildren = parent.children.filter(
    (childIdx) => !skel.bone(childIdx).auxiliary,
  );
  return parentNonAuxChildren.length === 1 && parentNonAuxChildren[0] === bone.index;
}

// ─── Public mark helpers ─────────────────────────────────────────────────

export function markAuxiliary(
  skel: Skeleton,
  _weightsInfo: Map<number, boolean> | null,
  eps: number,
): void {
  for (const bone of skel.bones) {
    const name = bone.name.toLowerCase();

    if (isFingerHelperLikeName(name)) {
      bone.auxiliary = true;
      continue;
    }

    if (AUX_PATTERNS.some((pat) => name.includes(pat))) {
      bone.auxiliary = true;
      continue;
    }

    if (bone.length < eps) {
      let isCritical = [...CRITICAL_JOINTS].some((kw) => name.includes(kw));
      if (isCritical) {
        if (_isSubdivisionBone(name) || name.includes('part')) {
          isCritical = false;
        }
      }

      let isOriginRoot = false;
      const isRootLike =
        bone.parent === null ||
        bone.parent < 0 ||
        (bone.parent !== null &&
          bone.parent >= 0 &&
          skel.bone(bone.parent).auxiliary &&
          skel.bone(bone.parent).parent === null);
      if (isRootLike) {
        const pos = boneWorldPos(bone);
        const distFromOrigin = pos.length();
        if (distFromOrigin < 0.01) {
          let hasOriginChildWithLength = false;
          for (const childIdx of bone.children) {
            const child = skel.bone(childIdx);
            const childPos = boneWorldPos(child);
            if (childPos.length() < 0.01 && child.length >= eps) {
              hasOriginChildWithLength = true;
              break;
            }
          }
          if (!hasOriginChildWithLength) isOriginRoot = true;
        }
      }

      if (_isProtectedGenericLeafEndpoint(skel, bone, eps, true)) {
        continue;
      }

      if (!name.includes('root') && !isCritical && !isOriginRoot) {
        bone.auxiliary = true;
        continue;
      }
    }
  }

  _markHelperLeafBones(skel);
  _markInlinePassthroughBones(skel);
}

export function markAuxiliaryByGeometry(
  skel: Skeleton,
  _spatialData: SkeletonSpatialData | null = null,
  mainBoneIndices: Set<number> | null = null,
): number[] {
  const marked: number[] = [];

  const positions = new Map<number, Vector3>();
  for (const bone of skel.bones) {
    positions.set(bone.index, boneWorldPos(bone));
  }

  let mainPositions: Vector3[];
  if (mainBoneIndices && mainBoneIndices.size >= 3) {
    mainPositions = [];
    for (const i of mainBoneIndices) {
      const p = positions.get(i);
      if (p) mainPositions.push(p);
    }
  } else {
    mainPositions = [...positions.values()];
  }

  const bboxMin = new Vector3(Infinity, Infinity, Infinity);
  const bboxMax = new Vector3(-Infinity, -Infinity, -Infinity);
  for (const p of mainPositions) {
    bboxMin.min(p);
    bboxMax.max(p);
  }
  const bboxSize = bboxMax.clone().sub(bboxMin);
  const bboxCenter = bboxMin.clone().add(bboxMax).multiplyScalar(0.5);

  const bboxSizeSafe = new Vector3(
    bboxSize.x < 1.0 ? 1.0 : bboxSize.x,
    bboxSize.y < 1.0 ? 1.0 : bboxSize.y,
    bboxSize.z < 1.0 ? 1.0 : bboxSize.z,
  );

  const positionEps = Math.max(
    0.01,
    Math.min(bboxSizeSafe.x, bboxSizeSafe.y, bboxSizeSafe.z) * 0.01,
  );

  const bboxDiagonal = bboxSize.length();
  const outerLimit = bboxDiagonal * 1.5;

  const leafPositionSet = new Set<string>();
  for (const bone of skel.bones) {
    if (bone.auxiliary) continue;
    const nonAux = bone.children.filter((c) => !skel.bone(c).auxiliary);
    if (nonAux.length === 0) {
      const pos = positions.get(bone.index)!;
      leafPositionSet.add(
        `${pos.x.toFixed(3)},${pos.y.toFixed(3)},${pos.z.toFixed(3)}`,
      );
    }
  }

  const leafPositions: Vector3[] = [];
  for (const key of leafPositionSet) {
    const [x, y, z] = key.split(',').map(Number);
    leafPositions.push(new Vector3(x, y, z));
  }

  for (const bone of skel.bones) {
    if (bone.auxiliary) continue;
    const pos = positions.get(bone.index)!;

    let markedByIk = false;
    for (const leafPos of leafPositions) {
      const dist = pos.distanceTo(leafPos);
      if (dist > 0 && dist < positionEps) {
        if (bone.name.toLowerCase().includes('goal')) {
          bone.auxiliary = true;
          marked.push(bone.index);
          markedByIk = true;
          break;
        }
      }
    }
    if (markedByIk) continue;

    const nonAuxChildren = bone.children.filter(
      (c) => !skel.bone(c).auxiliary,
    );
    const isLeaf = nonAuxChildren.length === 0;

    if (isLeaf && bone.length < positionEps) {
      if (mainBoneIndices !== null && mainBoneIndices.has(bone.index)) continue;
      bone.auxiliary = true;
      marked.push(bone.index);
      continue;
    }

    const distFromCenter = pos.distanceTo(bboxCenter);
    if (distFromCenter > outerLimit) {
      if (mainBoneIndices !== null && mainBoneIndices.has(bone.index)) continue;
      bone.auxiliary = true;
      marked.push(bone.index);
      continue;
    }

    const originDist = pos.length();
    if (originDist < positionEps && bone.depth >= 2) {
      if (mainBoneIndices !== null && !mainBoneIndices.has(bone.index)) {
        bone.auxiliary = true;
        marked.push(bone.index);
        continue;
      }
    }
  }

  return marked;
}

export function suggestAccessoryBranchExclusions(
  skel: Skeleton,
  mainBoneIndices: Set<number>,
): string[] {
  const suggestions = new Set<string>();
  const visited = new Set<number>();

  function isRecognizedLimbAnchor(idx: number): boolean {
    if (!mainBoneIndices.has(idx)) return false;
    const nameLower = skel.bone(idx).name.toLowerCase();
    return (
      isArmLikeName(nameLower) ||
      isHandLikeName(nameLower) ||
      isLegLikeName(nameLower)
    );
  }

  function isDirectLimbLeafDecoration(bone: Bone): boolean {
    if (bone.parent === null) return false;
    const nonAuxChildren = bone.children.filter((childIdx) => !skel.bone(childIdx).auxiliary);
    if (nonAuxChildren.length !== 0) return false;
    if (!isRecognizedLimbAnchor(bone.parent)) return false;

    const nameLower = bone.name.toLowerCase();
    return (
      isAccessoryBranchLikeName(nameLower) ||
      nameLower.includes('dummy') ||
      nameLower.includes('ダミー') ||
      (nameLower.includes('finger') && !nameLower.includes('carpal')) ||
      (nameLower.includes('leg') &&
        (nameLower.includes('upper') ||
          nameLower.includes('thigh') ||
          nameLower.includes('ctr')))
    );
  }

  for (const bone of skel.bones) {
    if (mainBoneIndices.has(bone.index)) continue;
    const nonAuxChildren = bone.children.filter((childIdx) => !skel.bone(childIdx).auxiliary);
    if (nonAuxChildren.length !== 0) continue;
    if (
      !isAccessoryBranchLikeName(bone.name) &&
      !isDirectLimbLeafDecoration(bone)
    ) {
      continue;
    }

    suggestions.add(bone.name);
    visited.add(bone.index);
  }

  function resolveMainAttachmentParent(startIdx: number): number | null {
    let currentParent = skel.bone(startIdx).parent;

    while (currentParent !== null) {
      if (mainBoneIndices.has(currentParent)) return currentParent;

      const parentBone = skel.bone(currentParent);
      if (!parentBone.auxiliary) return null;

      currentParent = parentBone.parent;
    }

    return null;
  }

  for (const bone of skel.bones) {
    if (visited.has(bone.index)) continue;
    if (mainBoneIndices.has(bone.index)) continue;
    const attachmentParent = resolveMainAttachmentParent(bone.index);
    if (attachmentParent === null) continue;

    const rootNameLower = bone.name.toLowerCase();
    const parentNameLower = skel.bone(attachmentParent).name.toLowerCase();
    const branchFromHead = isHeadLikeName(parentNameLower);
    const rootLooksAccessory = isAccessoryBranchLikeName(rootNameLower);
    if (!rootLooksAccessory && !branchFromHead) continue;

    const branch: number[] = [];
    const stack = [bone.index];
    let touchesMainBranch = false;

    while (stack.length > 0) {
      const idx = stack.pop()!;
      if (visited.has(idx)) continue;
      visited.add(idx);

      if (mainBoneIndices.has(idx)) {
        touchesMainBranch = true;
        continue;
      }

      const branchBone = skel.bone(idx);
      branch.push(idx);
      for (const childIdx of branchBone.children) {
        if (mainBoneIndices.has(childIdx)) {
          touchesMainBranch = true;
          continue;
        }
        stack.push(childIdx);
      }
    }

    if (touchesMainBranch || branch.length === 0) continue;

    const branchLooksStructuredLimb = branch.some((idx) => {
      const nameLower = skel.bone(idx).name.toLowerCase();
      return (
        isArmLikeName(nameLower) ||
        isHandLikeName(nameLower) ||
        isLegLikeName(nameLower)
      );
    });

    if (branchFromHead) {
      if (branchLooksStructuredLimb) continue;
      for (const idx of branch) {
        suggestions.add(skel.bone(idx).name);
      }
      continue;
    }

    const branchLooksAccessory = branch.every((idx) => {
      const nameLower = skel.bone(idx).name.toLowerCase();
      return isAccessoryBranchLikeName(nameLower);
    });
    if (!branchLooksAccessory) continue;

    for (const idx of branch) {
      suggestions.add(skel.bone(idx).name);
    }
  }

  return [...suggestions];
}

// ─── PCA ─────────────────────────────────────────────────────────────────

function eigenSymmetric3x3(
  m00: number, m01: number, m02: number,
  m11: number, m12: number, m22: number,
): { values: number[]; vectors: Vector3[] } {
  const a = [
    [m00, m01, m02],
    [m01, m11, m12],
    [m02, m12, m22],
  ];
  const v = [
    [1, 0, 0],
    [0, 1, 0],
    [0, 0, 1],
  ];

  for (let iter = 0; iter < 50; iter++) {
    let maxVal = 0;
    let p = 0;
    let q = 1;
    for (let i = 0; i < 3; i++) {
      for (let j = i + 1; j < 3; j++) {
        if (Math.abs(a[i][j]) > maxVal) {
          maxVal = Math.abs(a[i][j]);
          p = i;
          q = j;
        }
      }
    }
    if (maxVal < 1e-12) break;

    const app = a[p][p];
    const aqq = a[q][q];
    const apq = a[p][q];

    const theta =
      Math.abs(app - aqq) < 1e-15
        ? Math.PI / 4
        : 0.5 * Math.atan2(2 * apq, app - aqq);

    const c = Math.cos(theta);
    const s = Math.sin(theta);

    const newA: number[][] = a.map((row) => [...row]);
    newA[p][p] = c * c * app + 2 * s * c * apq + s * s * aqq;
    newA[q][q] = s * s * app - 2 * s * c * apq + c * c * aqq;
    newA[p][q] = 0;
    newA[q][p] = 0;

    for (let r = 0; r < 3; r++) {
      if (r === p || r === q) continue;
      const arp = a[r][p];
      const arq = a[r][q];
      newA[r][p] = c * arp + s * arq;
      newA[p][r] = newA[r][p];
      newA[r][q] = -s * arp + c * arq;
      newA[q][r] = newA[r][q];
    }

    for (let i = 0; i < 3; i++) {
      for (let j = 0; j < 3; j++) {
        a[i][j] = newA[i][j];
      }
    }

    for (let r = 0; r < 3; r++) {
      const vp = v[r][p];
      const vq = v[r][q];
      v[r][p] = c * vp + s * vq;
      v[r][q] = -s * vp + c * vq;
    }
  }

  return {
    values: [a[0][0], a[1][1], a[2][2]],
    vectors: [
      new Vector3(v[0][0], v[1][0], v[2][0]),
      new Vector3(v[0][1], v[1][1], v[2][1]),
      new Vector3(v[0][2], v[1][2], v[2][2]),
    ],
  };
}

export function computePcaAxes(
  skel: Skeleton,
): [Vector3, Vector3, Vector3] {
  const positions: Vector3[] = skel.bones.map((b) => boneWorldPos(b));
  const n = positions.length;
  if (n === 0) {
    return [new Vector3(1, 0, 0), new Vector3(0, 1, 0), new Vector3(0, 0, 1)];
  }

  const mean = new Vector3();
  for (const p of positions) mean.add(p);
  mean.divideScalar(n);

  let c00 = 0, c01 = 0, c02 = 0, c11 = 0, c12 = 0, c22 = 0;
  for (const p of positions) {
    const dx = p.x - mean.x;
    const dy = p.y - mean.y;
    const dz = p.z - mean.z;
    c00 += dx * dx;
    c01 += dx * dy;
    c02 += dx * dz;
    c11 += dy * dy;
    c12 += dy * dz;
    c22 += dz * dz;
  }
  const inv = 1 / (n - 1 || 1);
  c00 *= inv; c01 *= inv; c02 *= inv;
  c11 *= inv; c12 *= inv; c22 *= inv;

  const { values, vectors } = eigenSymmetric3x3(c00, c01, c02, c11, c12, c22);

  const order = [0, 1, 2].sort((a, b) => values[b] - values[a]);
  return [vectors[order[0]], vectors[order[1]], vectors[order[2]]];
}

// ─── Exclude pattern matching ────────────────────────────────────────────

function _isSeparator(char: string): boolean {
  return char === '_' || char === '-' || char === '.';
}

function _isWordBoundary(char: string): boolean {
  return (
    _isSeparator(char) ||
    (char >= 'A' && char <= 'Z') ||
    (char >= '0' && char <= '9')
  );
}

function _matchesExcludePattern(name: string, pattern: string): boolean {
  const nameLower = name.toLowerCase();
  const patternLower = pattern.toLowerCase();

  const hasPrefixSep = pattern.startsWith('_');
  const hasSuffixSep = pattern.endsWith('_');
  const basePattern = patternLower.replace(/^_+|_+$/g, '');

  if (hasPrefixSep || hasSuffixSep) {
    for (
      let i = 0;
      i <= nameLower.length - basePattern.length;
      i++
    ) {
      if (nameLower.substring(i, i + basePattern.length) === basePattern) {
        if (hasPrefixSep) {
          if (i === 0 || !_isSeparator(name[i - 1])) continue;
        }
        if (hasSuffixSep) {
          const endIdx = i + basePattern.length;
          if (endIdx >= name.length || !_isSeparator(name[endIdx])) continue;
        }
        return true;
      }
    }
    return false;
  }

  if (nameLower.startsWith(basePattern)) {
    if (name.length > basePattern.length) {
      if (_isWordBoundary(name[basePattern.length])) return true;
    } else {
      return true;
    }
  }

  for (let i = 1; i < name.length; i++) {
    if (name[i - 1] >= 'a' && name[i - 1] <= 'z' && name[i] >= 'A' && name[i] <= 'Z') {
      const suffix = name.substring(i);
      const suffixLower = suffix.toLowerCase();
      if (suffixLower.startsWith(basePattern)) {
        const afterPatternIdx = basePattern.length;
        if (afterPatternIdx >= suffix.length) return true;
        if (_isWordBoundary(suffix[afterPatternIdx])) return true;
      }
    }
  }

  for (const sep of ['_', '-']) {
    const sepPattern = sep + basePattern;
    const idx = nameLower.indexOf(sepPattern);
    if (idx >= 0) {
      const endIdx = idx + sepPattern.length;
      if (endIdx >= name.length) return true;
      if (_isWordBoundary(name[endIdx])) return true;
    }
  }

  return false;
}

// ─── Suggest excluded bones ──────────────────────────────────────────────

function _isTorsoAttachedPlaceholderJoint(skel: Skeleton, bone: Bone): boolean {
  if (!isGenericNumberedPlaceholderJointName(bone.name)) return false;
  if (bone.parent === null || bone.parent < 0) return false;

  const parentName = skel.bone(bone.parent).name;
  return (
    isSpineLikeName(parentName) ||
    isNeckLikeName(parentName) ||
    isHeadLikeName(parentName) ||
    isPelvisLikeName(parentName)
  );
}

export function suggestExcludedBones(skel: Skeleton): string[] {
  const suggestions = new Set<string>();
  const endpointProtectionEps = 1e-5;

  for (const bone of skel.bones) {
    if (bone.auxiliary) continue;
    const isDeformationHelper = isDeformationHelperLikeName(bone.name);
    if (
      (!bone.deforming && isGenericNumberedPlaceholderJointName(bone.name)) ||
      _isTorsoAttachedPlaceholderJoint(skel, bone) ||
      EXCLUDE_PATTERNS.some((pat) => _matchesExcludePattern(bone.name, pat)) ||
      isDeformationHelper
    ) {
      if (
        !isDeformationHelper &&
        (
          _isProtectedLimbBone(skel, bone) ||
          (
            !_isTorsoAttachedPlaceholderJoint(skel, bone) &&
            _isProtectedGenericLeafEndpoint(skel, bone, endpointProtectionEps)
          )
        )
      ) {
        continue;
      }
      suggestions.add(bone.name);
    }
  }

  const originThreshold = 1e-3;

  // Compute local-chain positions (without rootParentWorld) for origin
  // detection.  Python's GLTF importer bakes the ancestor transform into
  // the root bone's rest_local, so rest_world values are in the GLTF file's
  // coordinate space.  The TS extractor keeps rootParentWorld separate, so
  // using restWorld directly can shift positions when the armature has
  // scale/rotation, causing borderline bones to cross the 1e-3 threshold
  // differently.  Stripping rootParentWorld aligns the results.
  const rootParentInv = skel.rootParentWorld.clone().invert();
  const isAtOrigin = (bone: Bone): boolean => {
    const pos = boneWorldPos(bone).applyMatrix4(rootParentInv);
    return pos.length() < originThreshold;
  };

  const getChainToRoot = (bone: Bone): Bone[] => {
    const chain: Bone[] = [];
    let current = bone;
    while (current.parent !== null && current.parent >= 0) {
      const parent = skel.bone(current.parent);
      chain.push(parent);
      current = parent;
    }
    return chain;
  };

  const hasNonOriginDescendant = (bone: Bone): boolean => {
    for (const childIdx of bone.children) {
      const child = skel.bone(childIdx);
      if (!isAtOrigin(child)) return true;
      if (hasNonOriginDescendant(child)) return true;
    }
    return false;
  };

  for (const bone of skel.bones) {
    if (bone.auxiliary || suggestions.has(bone.name)) continue;
    if (!isAtOrigin(bone)) continue;

    const ancestors = getChainToRoot(bone);
    const allAncestorsAtOrigin = ancestors.every((a) => isAtOrigin(a));

    if (allAncestorsAtOrigin && ancestors.length >= 1) {
      if (bone.depth >= 2 && !hasNonOriginDescendant(bone)) {
        suggestions.add(bone.name);
      }
    }
  }

  return [...suggestions];
}

// ─── Axis / side helpers ─────────────────────────────────────────────────

export function axisFromLegChains(
  skel: Skeleton,
  legChains: number[][],
): Vector3 {
  if (legChains.length === 0) return new Vector3(0, -1, 0);
  const dirs: Vector3[] = [];
  for (const chain of legChains) {
    if (chain.length > 0) dirs.push(chainDirection(skel, chain));
  }
  if (dirs.length === 0) return new Vector3(0, -1, 0);
  const avg = new Vector3();
  for (const d of dirs) avg.add(d);
  avg.divideScalar(dirs.length);
  const norm = avg.length();
  return norm > 1e-8 ? avg.divideScalar(norm) : new Vector3(0, -1, 0);
}

export function lateralAxis(
  skel: Skeleton,
  _pelvisIdx: number,
  legChains: number[][],
): Vector3 {
  if (legChains.length >= 2) {
    const endA = boneWorldPos(skel.bone(legChains[0][legChains[0].length - 1]));
    const endB = boneWorldPos(skel.bone(legChains[1][legChains[1].length - 1]));
    const axis = endB.clone().sub(endA);
    const norm = axis.length();
    if (norm > 1e-8) return axis.divideScalar(norm);
  }
  const [, secondary] = computePcaAxes(skel);
  return secondary;
}

export function assignSide(
  axis: Vector3,
  pelvisPos: Vector3,
  chain: number[],
  skel: Skeleton,
): string {
  const endPos = boneWorldPos(skel.bone(chain[chain.length - 1]));
  const side = endPos.clone().sub(pelvisPos).dot(axis);
  return side > 0 ? 'Left' : 'Right';
}

export function chainSideHint(
  skel: Skeleton,
  chain: number[],
): string | null {
  let leftHits = 0;
  let rightHits = 0;
  for (const idx of chain) {
    const bone = skel.bone(idx);
    if (bone.isLeft) leftHits++;
    else if (bone.isRight) rightHits++;
  }
  if (leftHits > rightHits) return 'Left';
  if (rightHits > leftHits) return 'Right';
  return null;
}

// ─── Legacy score_role (deprecated) ──────────────────────────────────────

export function scoreRole(
  skel: Skeleton,
  bone: Bone,
  role: string,
  expectedDir: Vector3 | null,
  symmetry: number,
  lengthRatio: number,
  namingHints: string[],
  weights?: Record<string, number>,
  lateralSpread?: number,
  centerAlignment?: number,
): ScoreBreakdown {
  let topology = topologyScore(
    bone.parent !== null,
    EXPECTED_CHILD_COUNTS[role] ?? [0, 10],
    bone.children.filter((c) => !skel.bone(c).auxiliary).length,
    true,
  );

  if (centerAlignment !== undefined) {
    topology = 0.7 * topology + 0.3 * centerAlignment;
  }

  let finalSymmetry = symmetry;
  if (lateralSpread !== undefined) {
    finalSymmetry = 0.7 * symmetry + 0.3 * lateralSpread;
  }

  const naming = namingScore(bone.name, namingHints);
  const depth = depthScore(bone.depth, EXPECTED_DEPTHS[role] ?? [0, 15]);
  const direction =
    expectedDir !== null ? directionScore(bone.direction, expectedDir) : 0.5;

  const breakdown: ScoreBreakdown = {
    topology,
    symmetry: finalSymmetry,
    direction,
    lengthRatio,
    naming,
    depth,
    total: 0,
  };
  breakdown.total = totalScore(breakdown, weights);
  return breakdown;
}

// ─── Check-based scoring ─────────────────────────────────────────────────

export function scoreRoleWithChecks(
  skel: Skeleton,
  bone: Bone,
  role: string,
  chain?: number[] | null,
  mirrorChain?: number[] | null,
  expectedDir?: Vector3 | null,
  chainPosition?: number | null,
  chainLength?: number | null,
  roleToBone?: Record<string, string> | null,
  extra?: Record<string, any> | null,
): EvaluationResult {
  const ctx: CheckContext = {
    skeleton: skel,
    boneIdx: bone.index,
    role,
    chain: chain ?? undefined,
    mirrorChain: mirrorChain ?? undefined,
    expectedDirection: expectedDir ?? undefined,
    chainPosition: chainPosition ?? undefined,
    chainLength: chainLength ?? undefined,
    roleToBone: roleToBone ?? undefined,
    extra: extra ?? undefined,
  };
  return evaluateRole(ctx);
}

// ─── Evaluation → ScoreBreakdown adapter ─────────────────────────────────

export function evaluationToBreakdown(
  evalResult: EvaluationResult,
): ScoreBreakdown {
  let topology = 0.5;
  let symmetry = 0.5;
  let direction = 0.5;
  let lengthRatio = 0.5;
  let naming = 0.0;
  let depth = 0.5;

  for (const [check, result] of evalResult.results) {
    switch (check.id) {
      case 'naming_match':
        naming = result.score;
        break;
      case 'child_count':
      case 'parent_role':
      case 'linear_chain':
        topology = (topology + result.score) / 2;
        break;
      case 'depth':
        depth = result.score;
        break;
      case 'direction':
        direction = result.score;
        break;
      case 'symmetry':
        symmetry = result.score;
        break;
      case 'length_ratio':
        lengthRatio = result.score;
        break;
      case 'lateral_spread':
        symmetry = 0.7 * symmetry + 0.3 * result.score;
        break;
      case 'center_alignment':
        topology = 0.7 * topology + 0.3 * result.score;
        break;
    }
  }

  return {
    topology,
    symmetry,
    direction,
    lengthRatio,
    naming,
    depth,
    total: evalResult.score,
  };
}

// ─── score_candidate — THE scoring function ──────────────────────────────

export function scoreCandidate(
  skel: Skeleton,
  boneIdx: number,
  role: string,
  chain?: number[] | null,
  mirrorChain?: number[] | null,
  chainPosition?: number | null,
  expectedDirection?: Vector3 | null,
  roleToBone?: Record<string, string> | null,
  sideConvention?: SideConvention | null,
  spatialData?: SkeletonSpatialData | null,
  useSpatialSideFallback = false,
  scoringProfile?: ScoringProfile | null,
): CandidateInfo {
  const bone = skel.bone(boneIdx);
  const chainLength = chain ? chain.length : undefined;

  const extra: Record<string, any> = {};
  if (spatialData != null) extra['spatial_data'] = spatialData;
  if (useSpatialSideFallback) extra['use_spatial_side_fallback'] = true;
  if (scoringProfile != null) extra['scoring_profile'] = scoringProfile;

  const ctx: CheckContext = {
    skeleton: skel,
    boneIdx,
    role,
    chain: chain ?? undefined,
    mirrorChain: mirrorChain ?? undefined,
    chainPosition: chainPosition ?? undefined,
    chainLength: chainLength ?? undefined,
    expectedDirection: expectedDirection ?? undefined,
    roleToBone: roleToBone ?? undefined,
    sideConvention: sideConvention ?? undefined,
    extra,
  };

  const evalResult = evaluateRole(ctx);

  return CandidateInfo.fromEvaluationResult(bone.name, evalResult);
}

// ─── Prune inline twist bones ────────────────────────────────────────────

export function pruneInlineTwistBones(
  skel: Skeleton,
  chain: number[],
): number[] {
  if (chain.length < 3) return chain;

  const isTwistName = (name: string): boolean => {
    const lowered = name.toLowerCase();
    return lowered.includes('twist') || lowered.includes('roll');
  };

  const isImportantJoint = (name: string): boolean => {
    const lowered = name.toLowerCase();
    return [...JOINT_KEYWORDS].some((kw) => lowered.includes(kw));
  };

  let totalChainLength = 0;
  for (let i = 0; i < chain.length - 1; i++) {
    const p1 = boneWorldPos(skel.bone(chain[i]));
    const p2 = boneWorldPos(skel.bone(chain[i + 1]));
    totalChainLength += p1.distanceTo(p2);
  }

  const minSignificantLength = totalChainLength * 0.15;

  let startIdx = chain[0];
  let startI = 0;

  if (chain.length >= 3) {
    const startName = skel.bone(startIdx).name;
    if (isTwistName(startName)) {
      const v1 = boneWorldPos(skel.bone(chain[1]))
        .clone()
        .sub(boneWorldPos(skel.bone(startIdx)));
      const v2 = boneWorldPos(skel.bone(chain[2]))
        .clone()
        .sub(boneWorldPos(skel.bone(chain[1])));
      const n1 = v1.length();
      const n2 = v2.length();
      if (n1 > 1e-6 && n2 > 1e-6) {
        v1.divideScalar(n1);
        v2.divideScalar(n2);
        const dot = Math.max(-1.0, Math.min(1.0, v1.dot(v2)));
        const angle = Math.acos(dot) * (180 / Math.PI);
        if (angle < PERFECT_INLINE_ANGLE) {
          const nextName = skel.bone(chain[1]).name.toLowerCase();
          if (
            !['hand', 'wrist', 'wrise', 'cannon'].some((tok) =>
              nextName.includes(tok),
            )
          ) {
            startIdx = chain[1];
            startI = 1;
          }
        }
      }
    }
  }

  const pruned = [startIdx];

  for (let i = startI + 1; i < chain.length - 1; i++) {
    const prevIdx = pruned[pruned.length - 1];
    const currIdx = chain[i];
    const nextIdx = chain[i + 1];
    const currName = skel.bone(currIdx).name;

    if (isImportantJoint(currName) && !_isSubdivisionBone(currName)) {
      pruned.push(currIdx);
      continue;
    }

    const prevPos = boneWorldPos(skel.bone(prevIdx));
    const currPos = boneWorldPos(skel.bone(currIdx));
    const nextPos = boneWorldPos(skel.bone(nextIdx));

    const v1 = currPos.clone().sub(prevPos);
    const v2 = nextPos.clone().sub(currPos);
    const n1 = v1.length();
    const n2 = v2.length();

    if (n1 < 1e-6 || n2 < 1e-6) {
      pruned.push(currIdx);
      continue;
    }

    if (
      n1 >= minSignificantLength &&
      n2 >= minSignificantLength &&
      !_isSubdivisionBone(currName)
    ) {
      pruned.push(currIdx);
      continue;
    }

    v1.divideScalar(n1);
    v2.divideScalar(n2);
    const dot = Math.max(-1.0, Math.min(1.0, v1.dot(v2)));
    const angle = Math.acos(dot) * (180 / Math.PI);
    const currLen = skel.bone(currIdx).length;
    const neighborLen = Math.max(
      skel.bone(prevIdx).length,
      skel.bone(nextIdx).length,
      1e-6,
    );
    const isInline = angle < PERFECT_INLINE_ANGLE;
    const isShort = currLen < 0.6 * neighborLen;
    const isClavicle = (ROLE_TERMS['Clavicle'] ?? []).some((t) =>
      currName.includes(t),
    );

    if (
      isInline &&
      (isTwistName(currName) ||
        isShort ||
        isClavicle ||
        _isSubdivisionBone(currName))
    ) {
      continue;
    }

    pruned.push(currIdx);
  }

  pruned.push(chain[chain.length - 1]);
  return pruned.length >= 2 ? pruned : chain;
}
